import { tz } from "@date-fns/tz";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";

import { axiosInstance, TRADEA } from "api";
import { getSelectedPowerPlant, useAppSelector } from "store";

import { usePricesDateStr } from "../prices/prices";

export type TradeaIndex = "FIX1" | "CRO";
export type CroSource = "settled" | "preliminary" | "forecast";

export interface EarningsHour {
  time: string; // "HH:mm"
  forecastKwh: number | null;
  producedKwh: number | null;
  feedInKwh: number | null;
  fix1: number | null;
  cro: number | null;
  rdnPln: number | null;
  psePln: number | null;
  totalPln: number | null;
  tradeaPln: number | null;
  ourForecastKwh: number | null;
  expectedTradeaPln: number | null;
  expectedOursPln: number | null;
  croSource: CroSource | null;
  complete: boolean;
}

export interface EarningsQuarter {
  time: string; // "HH:mm"
  start: string; // ISO UTC
  fix1: number | null;
  cro: number | null;
  croSource: CroSource | null;
  forecastKwh: number | null;
  producedKwh: number | null;
  rdnPln: number | null;
  psePln: number | null;
  totalPln: number | null;
  expectedTradeaPln: number | null;
  ourForecastKwh: number | null;
  expectedOursPln: number | null;
}

export interface EarningsDay {
  configured: true;
  date: string;
  // "market" is the Tradea contract (RDN + PSE legs); "fixed" is a contract
  // rate per MWh, where those two legs do not exist and are null rather than
  // zero. A fixed-price day also has no settlement index and no Tradea report.
  mode: "market" | "fixed";
  tariff: { pricePlnPerMwh: number; validFrom: string; validTo: string | null } | null;
  index: TradeaIndex | null;
  ppes: string[];
  fix1Source: "tradea" | "tge-hourly" | null;
  croAssumption: { spread: number; source: "model" | "rdn" } | null;
  hours: EarningsHour[];
  quarters: EarningsQuarter[];
  totals: {
    forecastKwh: number;
    ourForecastKwh: number | null;
    producedKwh: number;
    rdnPln: number | null;
    psePln: number | null;
    // Production split the way it was paid: sold day-ahead at FIX1 and the
    // rest at CRO (negative where a FIX1 day fell short of PO); they add up to
    // producedKwh. Null on a fixed-price day; absent from servers before 2026-10-19.
    rdnKwh?: number | null;
    pseKwh?: number | null;
    totalPln: number;
    tradeaPln: number | null;
    expectedTradeaPln: number | null;
    expectedOursPln: number | null;
  };
  projectedPln: number | null;
  projectedOursPln: number | null;
  hasForecast: boolean;
  settled: boolean;
}

export type EarningsGrouping = "month" | "year" | "total";

export interface EarningsBucket {
  key: string; // "2026-08-15" | "2026-08" | "2026"
  from: string;
  to: string;
  days: number;
  index: TradeaIndex | "mixed" | null;
  mode: "market" | "fixed";
  // The contract rate, where any day in the bucket was paid one (null for a market bucket).
  tariff?: { pricePlnPerMwh: number | null; mixed: boolean; provisional: boolean } | null;
  // Tradea's PO over every day, CRO days included — not what was sold; that is rdnKwh.
  forecastKwh: number;
  producedKwh: number;
  rdnPln: number;
  psePln: number;
  rdnKwh?: number;
  pseKwh?: number;
  totalPln: number;
  tradeaPln: number | null;
  settled: boolean;
  hasProduction: boolean;
}

export interface EarningsSummary {
  configured: true;
  // "fixed" summaries carry zeroes in the RDN/PSE columns; don't render them.
  mode: "market" | "fixed";
  grouping: EarningsGrouping;
  from: string;
  to: string;
  buckets: EarningsBucket[];
  totals: {
    forecastKwh: number;
    producedKwh: number;
    rdnPln: number;
    psePln: number;
    rdnKwh?: number;
    pseKwh?: number;
    totalPln: number;
    comparedDays: number;
    oursOnComparedDays: number | null;
    tradeaPln: number | null;
    diffPln: number | null;
    diffPct: number | null;
  };
}

export type EarningsSummaryResponse =
  | EarningsSummary
  | { configured: false; grouping: EarningsGrouping };

export type EarningsResponse =
  | EarningsDay
  | { configured: false; date: string };

// Settlement estimate for the selected plant and day: what Tradea pays for
// the production, split into the RDN leg and the PSE (imbalance) leg.
export const useGetEarnings = (date?: Date, enabled = true) => {
  const dateStr = usePricesDateStr(date);
  const selectedPowerPlant = useAppSelector(getSelectedPowerPlant);
  const meterId = selectedPowerPlant?.meterId;
  return useQuery<EarningsResponse>({
    queryKey: [TRADEA.EARNINGS(meterId ?? ""), dateStr],
    queryFn: async () => {
      const { data } = await axiosInstance.get<EarningsResponse>(
        TRADEA.EARNINGS(meterId!),
        { params: { date: dateStr } },
      );
      return data;
    },
    enabled: enabled && Boolean(meterId),
    // Production and the imbalance price both move every quarter hour.
    refetchInterval: 60_000,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

// Bucketed totals: days of a month, months of a year, or years, with the
// running difference against Tradea's own report where it has arrived.
export const useGetEarningsSummary = (
  grouping: EarningsGrouping,
  date: Date,
  enabled = true,
) => {
  const selectedPowerPlant = useAppSelector(getSelectedPowerPlant);
  const meterId = selectedPowerPlant?.meterId;
  const dateStr = format(date, "yyyy-MM-dd", {
    in: tz(selectedPowerPlant?.timezone || "Europe/Warsaw"),
  });
  return useQuery<EarningsSummaryResponse>({
    queryKey: [TRADEA.EARNINGS_SUMMARY(meterId ?? ""), grouping, dateStr],
    queryFn: async () => {
      const { data } = await axiosInstance.get<EarningsSummaryResponse>(
        TRADEA.EARNINGS_SUMMARY(meterId!),
        { params: { grouping, date: dateStr } },
      );
      return data;
    },
    enabled: enabled && Boolean(meterId),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
};
